// ══════════════════════════════════════════════════════════════════════════════
// Aura Streaming Client (project_aura_streaming_ux_improvement.md)
// ══════════════════════════════════════════════════════════════════════════════
// Generic SSE transport, additive alongside the blocking queryAuraOrchestrator()
// in auraClient.ts (which is unchanged and remains the fallback path). Works
// against the real aura-orchestrator URL once it's deployed with opt-in
// streaming support (request body `stream: true`, gated server-side by
// AURA_ENABLE_STREAMING) — see index.ts's runOrchestration/respond().
// ══════════════════════════════════════════════════════════════════════════════

export type AuraStreamStage = 'understanding' | 'searching' | 'comparing' | 'writing';

export interface AuraStreamHandlers {
  onStage?: (stage: AuraStreamStage, label: string, tMs: number) => void;
  onToken?: (delta: string, textSoFar: string) => void;
  onDone?: (payload: Record<string, unknown>) => void;
  onError?: (message: string) => void;
}

// Parses a text/event-stream body into { event, data } frames as they arrive,
// tolerant of a frame split across two chunk boundaries (buffers the remainder).
export async function streamAuraOrchestrator(
  url: string,
  body: Record<string, unknown>,
  handlers: AuraStreamHandlers,
): Promise<void> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(body),
  });

  if (!res.ok || !res.body) {
    handlers.onError?.(`stream request failed: ${res.status}`);
    return;
  }

  // Rollback safety net: if the server-side AURA_ENABLE_STREAMING kill switch is
  // off, the orchestrator silently ignores `stream:true` and returns its normal
  // blocking JSON body instead of an SSE stream — no client redeploy needed to
  // recover. Detect that here and treat it as an immediate 'done', not an error.
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('text/event-stream')) {
    try {
      const data = await res.json();
      handlers.onDone?.(data);
    } catch {
      handlers.onError?.('non-stream response was not valid JSON');
    }
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sepIndex: number;
    while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, sepIndex);
      buffer = buffer.slice(sepIndex + 2);

      const eventLine = frame.split('\n').find((l) => l.startsWith('event: '));
      const dataLine = frame.split('\n').find((l) => l.startsWith('data: '));
      if (!eventLine || !dataLine) continue;

      const event = eventLine.slice('event: '.length).trim();
      let data: Record<string, unknown>;
      try {
        data = JSON.parse(dataLine.slice('data: '.length));
      } catch {
        continue;
      }

      switch (event) {
        case 'stage':
          handlers.onStage?.(data.stage as AuraStreamStage, data.label as string, data.t_ms as number);
          break;
        case 'token':
          handlers.onToken?.(data.delta as string, data.text_so_far as string);
          break;
        case 'done':
          handlers.onDone?.(data);
          break;
        case 'error':
          handlers.onError?.(data.error as string);
          break;
        // 'debug_intent' is prototype-only telemetry, intentionally not surfaced to callers.
      }
    }
  }
}
